import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import deliveryFleetService from '../services/deliveryFleetService';
import 'mapbox-gl/dist/mapbox-gl.css';

const POLL_MS = 12000;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const STATUS_LABEL = {
  accepted: 'A driver is heading to pick up your order.',
  picked_up: 'Your order has been picked up and is on its way!',
  delivered: 'Delivered — thanks for your order!',
};

function LiveMap({ lat, lng }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!MAPBOX_TOKEN || lat == null || lng == null) return;
    let cancelled = false;
    import('mapbox-gl').then((mapboxgl) => {
      if (cancelled) return;
      mapboxgl.default.accessToken = MAPBOX_TOKEN;
      if (!mapRef.current) {
        mapRef.current = new mapboxgl.default.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [lng, lat],
          zoom: 13,
        });
        markerRef.current = new mapboxgl.default.Marker({ color: '#0d6efd' }).setLngLat([lng, lat]).addTo(mapRef.current);
      } else {
        markerRef.current.setLngLat([lng, lat]);
        mapRef.current.panTo([lng, lat]);
      }
    });
    return () => { cancelled = true; };
  }, [lat, lng]);

  if (!MAPBOX_TOKEN) return null;
  return <div ref={containerRef} style={{ width: '100%', height: 280, borderRadius: 8, overflow: 'hidden' }} />;
}

export default function DeliveryTrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let interval;
    const poll = () => {
      deliveryFleetService.getTracking(token).then(setData).catch(() => setError(true));
    };
    poll();
    interval = setInterval(poll, POLL_MS);
    return () => clearInterval(interval);
  }, [token]);

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center text-muted">This tracking link isn't valid.</div>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center align-items-start px-3 py-5" style={{ minHeight: '100vh', background: '#f4f6f9' }}>
      <div className="card shadow-sm" style={{ maxWidth: 480, width: '100%' }}>
        <div className="card-body p-4">
          {!data ? (
            <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
          ) : (
            <>
              <h5 className="mb-1">Order #{data.orderNumber}</h5>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                {[data.deliveryCity, data.deliveryState].filter(Boolean).join(', ')}
              </p>
              <div className="alert alert-primary" style={{ fontSize: 14 }}>
                {STATUS_LABEL[data.status] || 'Your order is being processed.'}
              </div>
              {data.etaToFactoryMinutes != null && data.status === 'accepted' && (
                <p className="text-muted" style={{ fontSize: 13 }}>Driver is about {data.etaToFactoryMinutes} min from pickup.</p>
              )}
              {data.currentLat != null && data.currentLng != null ? (
                <LiveMap lat={data.currentLat} lng={data.currentLng} />
              ) : (
                MAPBOX_TOKEN && <p className="text-muted text-center" style={{ fontSize: 13 }}>Live location will appear here once available.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
